import { useCallback, useEffect, useMemo, useState } from 'react'
import { getSensitiveMarks } from '../visionApi'
import { SINGLE_ITEM_TYPES } from './cameraControlRules'

const idOf = (item) => item.type + ' ' + item.key

/**
 * Story 213, reopening 3 (C15.3, H-C15-1, H-C15-4) — the single items protected, as one list of the three types: the
 * direct marks Sun keeps (three GET ?type= on opening, in the order of the selector) with the pending changes on top.
 * Protecting or removing sends nothing: `changes` is what "Save changes" adds to the batch of the screens. Undoing a
 * pending change (removing what was just protected, or protecting again what was just removed) leaves nothing pending.
 */
const useSensitiveItems = () => {
  const [stored, setStored] = useState([])
  const [added, setAdded] = useState([])
  const [removed, setRemoved] = useState([])

  useEffect(() => {
    let cancelled = false
    Promise.all(SINGLE_ITEM_TYPES.map(({ type }) => getSensitiveMarks(type)
      .then((marks) => (Array.isArray(marks) ? marks : []))
      .catch(() => [])))
      .then((lists) => {
        if (!cancelled) {
          setStored(lists.flat())
        }
      })
    return () => { cancelled = true }
  }, [])

  const list = useMemo(() => [...stored.filter((mark) => !removed.includes(idOf(mark))), ...added], [stored, added, removed])

  const isProtected = useCallback((item) => list.some((mark) => idOf(mark) === idOf(item)), [list])

  const protect = useCallback((item) => {
    const id = idOf(item)
    if (removed.includes(id)) {
      setRemoved((previous) => previous.filter((candidate) => candidate !== id))
    } else if (!stored.some((mark) => idOf(mark) === id) && !added.some((mark) => idOf(mark) === id)) {
      setAdded((previous) => [...previous, { type: item.type, key: item.key, name: item.name, sensitive: true }])
    }
  }, [stored, added, removed])

  const remove = useCallback((mark) => {
    const id = idOf(mark)
    if (added.some((candidate) => idOf(candidate) === id)) {
      setAdded((previous) => previous.filter((candidate) => idOf(candidate) !== id))
    } else {
      setRemoved((previous) => [...previous, id])
    }
  }, [added])

  const changes = useMemo(() => [
    ...stored.filter((mark) => removed.includes(idOf(mark))).map((mark) => ({ type: mark.type, key: mark.key, sensitive: false })),
    ...added.map((mark) => ({ type: mark.type, key: mark.key, sensitive: true }))
  ], [stored, added, removed])

  const discard = useCallback(() => {
    setAdded([])
    setRemoved([])
  }, [])

  return { list, isProtected, protect, remove, changes, hasChanges: changes.length > 0, discard }
}

export default useSensitiveItems
